import { useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  Group,
  NumberInput,
  Pagination,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import {
  IconDownload,
  IconChevronUp,
  IconChevronDown,
  IconSearch,
  IconSelector,
} from "@tabler/icons-react";
import {
  useAdminLabelerMetrics,
  type LabelerMetricsRow,
} from "@/hooks/useAdminLabelerMetrics";
import type { MetricsPeriod } from "@/utils/userInsights";

export type LabelerTaskType = "OUT_OF_STOCK_CONFIRMATION" | "COVERAGE_BETA";

const PAGE_SIZE = 50;

const PERIOD_OPTIONS = [
  { label: "Last 7 days", value: "last_7d" },
  { label: "Last 30 days", value: "last_30d" },
  { label: "All time", value: "all_time" },
];

function scoreGrade(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: "A", color: "teal" };
  if (score >= 80) return { grade: "B", color: "green" };
  if (score >= 70) return { grade: "C", color: "yellow" };
  if (score >= 60) return { grade: "D", color: "orange" };
  return { grade: "F", color: "red" };
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

type SortCol = Exclude<keyof LabelerMetricsRow, "id">;

type SortDir = "asc" | "desc";

type Column = {
  key: SortCol;
  label: string;
  render: (r: LabelerMetricsRow) => ReactNode;
  // Raw value written to the CSV export.
  csv: (r: LabelerMetricsRow) => string | number;
};

const count = (key: SortCol, label: string): Column => ({
  key,
  label,
  render: (r) => r[key] ?? 0,
  csv: (r) => r[key] ?? 0,
});

const EMAIL_COLUMN: Column = {
  key: "email",
  label: "Email",
  render: (r) => r.email,
  csv: (r) => r.email,
};

const VENDOR_COLUMN: Column = {
  key: "vendor",
  label: "Vendor",
  render: (r) => (
    <Text size="sm" c="dimmed" span>
      {r.vendor || "—"}
    </Text>
  ),
  csv: (r) => r.vendor ?? "",
};

const SCORE_COLUMN: Column = {
  key: "overall_score",
  label: "Score",
  render: (r) => {
    const { grade, color } = scoreGrade(r.overall_score);
    return (
      <Group gap={6}>
        <Text size="sm" fw={600}>
          {Math.round(r.overall_score)}
        </Text>
        <Badge size="xs" color={color} variant="light">
          {grade}
        </Badge>
      </Group>
    );
  },
  csv: (r) => r.overall_score,
};

// OOS Confirmation is scored per task; Coverage Beta is scored per box (a
// coverage task has many boxes), mirroring user-insights' _SCORING_OVERRIDES.
const COLUMNS: Record<LabelerTaskType, Column[]> = {
  OUT_OF_STOCK_CONFIRMATION: [
    EMAIL_COLUMN,
    VENDOR_COLUMN,
    SCORE_COLUMN,
    {
      key: "precision",
      label: "Precision",
      render: (r) => fmtPct(r.precision),
      csv: (r) => (r.precision * 100).toFixed(1),
    },
    {
      key: "avg_tasks_per_shift",
      label: "Avg Tasks/Shift",
      render: (r) => r.avg_tasks_per_shift.toFixed(1),
      csv: (r) => r.avg_tasks_per_shift.toFixed(1),
    },
    {
      key: "avg_task_time",
      label: "Avg Task Time",
      render: (r) => fmtTime(r.avg_task_time),
      csv: (r) => r.avg_task_time,
    },
    count("total_tasks_completed", "Total Tasks"),
    count("reviewed_tasks", "Reviewed"),
  ],
  COVERAGE_BETA: [
    EMAIL_COLUMN,
    VENDOR_COLUMN,
    SCORE_COLUMN,
    {
      key: "box_precision",
      label: "Box Precision",
      render: (r) => fmtPct(r.box_precision ?? 0),
      csv: (r) => ((r.box_precision ?? 0) * 100).toFixed(1),
    },
    {
      key: "avg_box_time",
      label: "Avg Box Time",
      render: (r) => fmtTime(r.avg_box_time ?? 0),
      csv: (r) => r.avg_box_time ?? 0,
    },
    count("boxes_completed", "Boxes"),
    count("boxes_reviewed", "Boxes Reviewed"),
    count("boxes_missed", "Boxes Missed"),
    count("total_tasks_completed", "Tasks"),
  ],
};

// Each column's CSV cell(s); the score column also gets a letter-grade cell.
function csvCells(c: Column, r: LabelerMetricsRow): (string | number)[] {
  if (c.key === "overall_score")
    return [c.csv(r), scoreGrade(r.overall_score).grade];
  return [c.csv(r)];
}

function downloadCsv(
  rows: LabelerMetricsRow[],
  columns: Column[],
  taskType: LabelerTaskType,
  period: MetricsPeriod,
) {
  const headers = columns.flatMap((c) =>
    c.key === "overall_score" ? [c.key, "grade"] : [c.key],
  );
  const escape = (v: string | number) => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const dataRows = rows.map((r) =>
    columns
      .flatMap((c) => csvCells(c, r))
      .map(escape)
      .join(","),
  );
  const csv = [headers.join(","), ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `labeler-metrics-${taskType.toLowerCase()}-${period}-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function sortRows(
  rows: LabelerMetricsRow[],
  col: SortCol,
  dir: SortDir,
): LabelerMetricsRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[col] ?? 0;
    const bv = b[col] ?? 0;
    if (av < bv) return -sign;
    if (av > bv) return sign;
    return 0;
  });
}

function SortIcon({
  col,
  active,
  dir,
}: {
  col: string;
  active: string;
  dir: SortDir;
}) {
  if (col !== active)
    return <IconSelector size={14} className="text-gray-400" />;
  return dir === "asc" ? (
    <IconChevronUp size={14} className="text-blue-600" />
  ) : (
    <IconChevronDown size={14} className="text-blue-600" />
  );
}

export const LabelerMetricsTab = ({
  taskType,
}: {
  taskType: LabelerTaskType;
}) => {
  const [period, setPeriod] = useState<MetricsPeriod>("last_30d");
  const [vendor, setVendor] = useState<string | null>(null);
  const [minScore, setMinScore] = useState<number | string>("");
  const [sortCol, setSortCol] = useState<SortCol>("overall_score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const columns = COLUMNS[taskType];
  const {
    data: rows = [],
    isLoading,
    isError,
  } = useAdminLabelerMetrics(taskType, period);

  const vendors = [
    ...new Set(rows.map((r) => r.vendor).filter(Boolean)),
  ].sort();
  // Rows are already scoped server-side to what the viewer may see, so a
  // single-vendor viewer (e.g. a team lead) has nothing to pick between.
  const showVendorFilter = vendors.length > 1;
  // Ignore a stale selection when a new period's rows no longer contain it.
  const activeVendor = vendor && vendors.includes(vendor) ? vendor : null;
  const query = search.trim().toLowerCase();

  const filtered = rows.filter((r) => {
    if (activeVendor && r.vendor !== activeVendor) return false;
    if (typeof minScore === "number" && r.overall_score < minScore)
      return false;
    if (query && !r.email.toLowerCase().includes(query)) return false;
    return true;
  });

  const sorted = sortRows(filtered, sortCol, sortDir);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(pageStart, pageStart + PAGE_SIZE);

  // Wraps a filter/sort setter so any change jumps back to the first page.
  const resetPage =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(1);
    };

  const handleSort = (col: SortCol) => {
    setPage(1);
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} h={40} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Text size="sm" c="red">
        Failed to load labeler metrics.
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Group justify="space-between" align="flex-end">
        <Group align="flex-end" gap="md">
          <div>
            <Text size="xs" fw={500} c="dimmed" mb={4}>
              Period
            </Text>
            <SegmentedControl
              size="xs"
              data={PERIOD_OPTIONS}
              value={period}
              onChange={(v) => resetPage(setPeriod)(v as MetricsPeriod)}
            />
          </div>
          <TextInput
            size="xs"
            label="Search"
            placeholder="Search by email"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => resetPage(setSearch)(e.currentTarget.value)}
            w={220}
          />
          {showVendorFilter && (
            <Select
              size="xs"
              label="Vendor"
              placeholder="All vendors"
              clearable
              data={vendors}
              value={activeVendor}
              onChange={resetPage(setVendor)}
              w={180}
            />
          )}
          <NumberInput
            size="xs"
            label="Min score"
            placeholder="0"
            min={0}
            max={100}
            value={minScore}
            onChange={resetPage(setMinScore)}
            w={100}
          />
        </Group>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconDownload size={14} />}
          onClick={() => downloadCsv(sorted, columns, taskType, period)}
        >
          Export CSV
        </Button>
      </Group>

      <div className="overflow-auto">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th key={c.key}>
                  <UnstyledButton
                    onClick={() => handleSort(c.key)}
                    className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-blue-600 select-none"
                  >
                    {c.label}
                    <SortIcon col={c.key} active={sortCol} dir={sortDir} />
                  </UnstyledButton>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pageRows.map((r) => (
              <Table.Tr key={r.id}>
                {columns.map((c) => (
                  <Table.Td key={c.key}>
                    <Text size="sm" component="div">
                      {c.render(r)}
                    </Text>
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
            {sorted.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Text size="sm" c="dimmed" ta="center" py="xl">
                    No labelers match the current filters.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>

      {sorted.length > 0 && (
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Showing {pageStart + 1}–{pageStart + pageRows.length} of{" "}
            {sorted.length} labelers
          </Text>
          {totalPages > 1 && (
            <Pagination
              size="sm"
              value={currentPage}
              onChange={setPage}
              total={totalPages}
            />
          )}
        </Group>
      )}
    </div>
  );
};
